/**
 * Supabase Auth — vervanger van de zelfgebouwde localStorage-auth.
 *
 * ─── WAAROM ─────────────────────────────────────────────────────────────────
 *
 * De huidige auth hasht wachtwoorden client-side met SHA-256 zonder salting en
 * bewaart de hashes in localStorage. Dat is een bekend 🔴-punt uit de
 * accounts-audit: leesbaar via DevTools, en zonder key-stretching triviaal te
 * brute-forcen.
 *
 * Belangrijker nog: **RLS kan niet werken zonder echte auth.** Elke policy in
 * `0001_secure_foundation.sql` bouwt op `auth.uid()`. Zolang de app zijn eigen
 * sessiebegrip heeft, is `auth.uid()` null en geeft de database niets vrij.
 * Auth omzetten is dus geen los verbeterpunt maar de voorwaarde voor de hele
 * beveiliging.
 *
 * Deze module is opzettelijk een dunne, framework-vrije laag: geen React, geen
 * store-kennis. De store mag hierop leunen, niet omgekeerd.
 */

import { supabase, type CurrentProfile } from './client';
import { error as logError } from '../../utils/logger';

/** Rollen die via registratie mogen worden aangevraagd. Nooit 'admin'. */
export type SignUpRole = 'learner' | 'parent' | 'teacher';
export interface SignUpInput {
  email: string;
  password: string;
  displayName: string;
  role: SignUpRole;
  /** Alleen zinvol voor learners: groep 3 t/m 8. */
  grade?: number;
  /**
   * Alleen zinvol voor teachers. Vrije tekst, mag leeg zijn.
   *
   * Leeg betekent "solo-leerkracht, geen schoolbinding" — dat is een geldige
   * situatie (bijles, thuisonderwijs) en geen ontbrekend gegeven.
   *
   * Dit veld ontbrak, waardoor het registratiescherm de school wél vroeg en
   * daarna weggooide. Een beheerder verifieerde vervolgens een naam zonder te
   * weten namens welke school. Zie migratie 0038.
   *
   * SINDS 0041 IS DIT UITSLUITEND DE AANVRAAG. Koos de leerkracht een school uit
   * het register, dan hoort `schoolId` gevuld te zijn en dit veld leeg.
   */
  schoolName?: string;
  /**
   * De school die de leerkracht KOOS uit het register (migratie 0041).
   *
   * WAAROM DIT EEN APART VELD IS EN GEEN NAAM
   *
   * Zolang een keuze en een aanvraag in hetzelfde veld landden, kon een
   * beheerder ze niet onderscheiden — en dat is precies het gegeven waarop hij
   * hoort te verifiëren. Een naam die de aanvrager zelf mocht opschrijven is
   * geen controleerbaar feit; een verwijzing naar een rij die wij hebben
   * opgenomen, is dat wel.
   *
   * De trigger controleert dit id tegen het register én tegen status
   * 'verified'. Een client die iets anders meestuurt, krijgt null en valt terug
   * op de aanvraagroute. Deze regel is netheid; die in de database is de regel.
   */
  schoolId?: string;
}

/**
 * Uitkomst van een auth-actie.
 *
 * Bewust hetzelfde patroon als het `boolean`-schrijfcontract elders in de
 * dataLayer: een mislukking is een WAARDE, geen exception die stil kan
 * verdwijnen in een `catch {}`. `message` is Nederlandstalig en veilig om aan
 * de gebruiker te tonen — het lekt geen serverdetails.
 */
export interface AuthResult {
  ok: boolean;
  message?: string;
}

/**
 * Vertaal Supabase-foutmeldingen naar begrijpelijk Nederlands.
 *
 * We geven bewust GEEN onderscheid tussen "e-mail bestaat niet" en "wachtwoord
 * fout": dat verschil laat een aanvaller e-mailadressen aftasten. Eén neutrale
 * melding voor beide.
 */
function toDutchMessage(raw: string): string {
  const lower = raw.toLowerCase();
  if (lower.includes('invalid login credentials')) {
    return 'E-mailadres of wachtwoord is niet juist.';
  }
  if (lower.includes('email not confirmed')) {
    return 'Je moet eerst je e-mailadres bevestigen. Check je mailbox.';
  }
  if (lower.includes('already registered') || lower.includes('already exists')) {
    return 'Er bestaat al een account met dit e-mailadres.';
  }
  if (lower.includes('password') && lower.includes('least')) {
    return 'Kies een wachtwoord van minimaal 8 tekens.';
  }
  // Let op de volgorde: de e-mailquota is iets ANDERS dan te veel
  // inlogpogingen. Supabase's ingebouwde mailer staat standaard op een paar
  // berichten per uur. "Wacht een minuut" is dan een misleidend advies — je
  // wacht een uur, of er hoort helemaal geen mail verstuurd te worden.
  if (lower.includes('email rate limit')) {
    return 'De e-maillimiet van de server is bereikt. Probeer het over een uur opnieuw.';
  }
  if (lower.includes('rate limit') || lower.includes('too many')) {
    return 'Te veel pogingen. Wacht een minuut en probeer het opnieuw.';
  }
  if (lower.includes('network') || lower.includes('fetch')) {
    return 'Geen verbinding met de server. Check je internetverbinding.';
  }
  // Supabase weigert sommige e-mailvormen. Voor leerlingen is dat een reëel
  // scenario: hun adres is synthetisch (`…@learners.meesterdjay.invalid`) en
  // niet elke validator accepteert het `.invalid`-domein.
  if (lower.includes('unable to validate email') || lower.includes('invalid email') || lower.includes('email address') || lower.includes('invalid format')) {
    return 'Deze gebruikersnaam wordt niet geaccepteerd. Probeer een andere.';
  }
  if (lower.includes('signups not allowed') || lower.includes('disabled')) {
    return 'Registreren staat momenteel uit. Neem contact op met de beheerder.';
  }
  return 'Er ging iets mis. Probeer het opnieuw.';
}

/**
 * Registreer een nieuw account.
 *
 * De rol gaat mee als metadata en wordt server-side door de
 * `handle_new_user`-trigger geklemd naar learner/parent/teacher. Een client die
 * `role: 'admin'` meestuurt krijgt gewoon een learner-profiel — de klem zit in
 * de database, niet hier.
 */
export async function signUp(input: SignUpInput): Promise<AuthResult> {
  const {
    error
  } = await supabase.auth.signUp({
    email: input.email.trim(),
    password: input.password,
    options: {
      // Waar de bevestigingslink naartoe wijst.
      //
      // Zonder dit gebruikt Supabase de Site URL uit het dashboard, en die
      // staat standaard op `http://localhost:3000`. Die link belandt dan in de
      // mailbox van een échte ouder en doet daar niets — een doodlopende
      // registratie die er voor de gebruiker uitziet als een defecte app.
      //
      // `window.location.origin` volgt automatisch de omgeving waar de
      // registratie vandaan komt: preview, productie of lokaal. Eén vaste
      // waarde zou altijd één van die drie breken.
      //
      // ⚠️ Dit werkt alléén als het adres in het Supabase-dashboard onder
      // Authentication → URL Configuration bij "Redirect URLs" staat. Staat het
      // er niet, dan valt Supabase stil terug op de Site URL — geen foutmelding,
      // gewoon de verkeerde link.
      emailRedirectTo: typeof window !== 'undefined' ? window.location.origin : undefined,
      data: {
        display_name: input.displayName.trim(),
        role: input.role,
        grade: input.grade != null ? String(input.grade) : '',
        // Alleen meesturen bij een leerkracht. De trigger klemt hier nog een
        // keer op (0038) — dat is bewust dubbel: deze regel is netheid, die
        // in de database is de regel.
        //
        // Een KEUZE en een AANVRAAG sluiten elkaar uit: staat er een id, dan
        // gaat de vrije tekst niet mee. Zouden ze beide meegaan, dan staat er
        // straks een school_id én een school_name en is het onderscheid waar
        // 0041 om begonnen was weer weg.
        school_name: input.role === 'teacher' && !input.schoolId ? (input.schoolName ?? '').trim() : '',
        school_id: input.role === 'teacher' ? (input.schoolId ?? '') : ''
      }
    }
  });
  if (error) {
    // Log de RUWE melding. `toDutchMessage` valt bewust terug op een vage tekst
    // zodat we niets over de server lekken — maar dan is een mislukte
    // registratie zonder dit logregel niet te diagnosticeren.
    logError('[SupabaseAuth] Registratie geweigerd', error.message);
    return {
      ok: false,
      message: toDutchMessage(error.message)
    };
  }
  return {
    ok: true
  };
}
export async function signIn(email: string, password: string): Promise<AuthResult> {
  const {
    error
  } = await supabase.auth.signInWithPassword({
    email: email.trim(),
    password
  });
  if (error) return {
    ok: false,
    message: toDutchMessage(error.message)
  };
  return {
    ok: true
  };
}
export async function signOut(): Promise<AuthResult> {
  const {
    error
  } = await supabase.auth.signOut();
  if (error) return {
    ok: false,
    message: toDutchMessage(error.message)
  };
  return {
    ok: true
  };
}

/**
 * Stuur een wachtwoordherstel-mail.
 *
 * ⚠️ `redirectTo` IS NIET GENOEG — DIT VRAAGT OOK EEN INSTELLING IN SUPABASE.
 *
 * De link in de mail wijst naar de origin waar de AANVRAAG vandaan kwam. Vraag je
 * het aan vanaf localhost, dan staat er localhost in de mail — dat is geen bug in
 * deze code maar het gevolg van `window.location.origin`.
 *
 * Supabase weigert bovendien elke `redirectTo` die niet in de allowlist staat, en
 * valt dan stil terug op de "Site URL" van het project. Zonder die twee
 * instellingen komt de gebruiker dus op een adres dat niet bestaat:
 *
 *   Authentication → URL Configuration
 *     Site URL:            https://beta.meesterdjay.nl
 *     Redirect URLs:       https://beta.meesterdjay.nl/**
 *                          http://localhost:5173/**
 *
 * Een pad is hier niet nodig en zou zelfs verkeerd zijn: deze app heeft geen
 * URL-router, dus `/wachtwoord-herstellen` komt nergens op uit. De tokens staan
 * in de HASH en die overleeft elk pad — `SetNewPasswordScreen` pikt ze op de root
 * op. Zie `src/accounts/logic/recoveryLink.ts`.
 */
export async function requestPasswordReset(email: string): Promise<AuthResult> {
  const {
    error
  } = await supabase.auth.resetPasswordForEmail(email.trim(), {
    redirectTo: `${window.location.origin}/`
  });
  // Bewust altijd 'ok': anders verklap je of dit e-mailadres bestaat.
  if (error && !error.message.toLowerCase().includes('not found')) {
    return {
      ok: false,
      message: toDutchMessage(error.message)
    };
  }
  return {
    ok: true
  };
}

/**
 * Zet een nieuw wachtwoord na het klikken op de herstellink uit de e-mail.
 *
 * WAAROM DIT NIET `changePassword` IS
 *
 * `changePassword` eist eerst het HUIDIGE wachtwoord, en dat is hier precies wat
 * de gebruiker niet heeft — dat is de reden dat hij de mail aanvroeg. De
 * herstellink is zelf het bewijs: Supabase heeft de tokens uit de hash omgezet in
 * een geldige sessie, en die sessie kwam alleen tot stand doordat iemand toegang
 * had tot het e-mailadres.
 *
 * Dus geen tweede controle, maar wél expliciet controleren dát er een sessie is.
 * Zonder die controle geeft `updateUser` bij een verlopen link een technische
 * Supabase-melding, en dan leest een dode link als een storing.
 */
export async function completePasswordReset(newPassword: string): Promise<AuthResult> {
  const {
    data
  } = await supabase.auth.getSession();
  if (!data.session) {
    return {
      ok: false,
      message: 'Deze herstellink is verlopen of al gebruikt. Vraag een nieuwe aan.'
    };
  }
  const {
    error
  } = await supabase.auth.updateUser({
    password: newPassword
  });
  if (error) {
    logError('[SupabaseAuth] Wachtwoord herstellen mislukt', error.message);
    return {
      ok: false,
      message: toDutchMessage(error.message)
    };
  }
  return {
    ok: true
  };
}

/**
 * Wijzig het wachtwoord van de ingelogde gebruiker.
 *
 * Supabase vraagt zelf NIET om het huidige wachtwoord — `updateUser` accepteert
 * elke geldige sessie. Dat is te ruim: wie een onbeheerd ingelogd apparaat
 * vindt (een tablet in de klas, een telefoon op tafel) kan dan het wachtwoord
 * overnemen en de rechtmatige eigenaar buitensluiten. Daarom verifiëren we
 * eerst het huidige wachtwoord met een losse inlogpoging.
 *
 * Die verificatie kost een extra rondje, maar is het waard: het is het verschil
 * tussen "je hebt dit apparaat even in handen" en "je kent dit account".
 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<AuthResult> {
  const {
    data
  } = await supabase.auth.getSession();
  const email = data.session?.user.email;
  if (!email) {
    return {
      ok: false,
      message: 'Je bent niet ingelogd.'
    };
  }
  const {
    error: verifyError
  } = await supabase.auth.signInWithPassword({
    email,
    password: currentPassword
  });
  if (verifyError) {
    return {
      ok: false,
      message: 'Het huidige wachtwoord is onjuist.'
    };
  }
  const {
    error
  } = await supabase.auth.updateUser({
    password: newPassword
  });
  if (error) {
    logError('[SupabaseAuth] Wachtwoord wijzigen mislukt', error.message);
    return {
      ok: false,
      message: toDutchMessage(error.message)
    };
  }
  return {
    ok: true
  };
}

/** Is er nu een geldige sessie? */
export async function hasSession(): Promise<boolean> {
  const {
    data
  } = await supabase.auth.getSession();
  return data.session != null;
}

/**
 * Abonneer op sessiewijzigingen (inloggen, uitloggen, token-refresh).
 *
 * Retourneert een unsubscribe-functie — gebruik die in een effect-cleanup, want
 * anders blijft er een listener hangen bij elke remount.
 */
export function onAuthChange(callback: (signedIn: boolean) => void): () => void {
  const {
    data
  } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(session != null);
  });
  return () => data.subscription.unsubscribe();
}
export type { CurrentProfile };
export { getCurrentProfile, getCurrentUserId } from './client';